import type { FeedDayGroup, FeedRow } from "../types";
import { calendarDaysBetween, dayKeyAndLabel } from "./calendar-date";
const dayMs = 24 * 60 * 60 * 1000;
export const activityTimeZone = "UTC";
export function formatRelative({
  iso,
  now,
  timeZone,
}: {
  readonly iso: string;
  readonly now: string;
  readonly timeZone: string;
}): string {
  const at = Date.parse(iso);
  const nowMs = Date.parse(now);

  if (Number.isNaN(at) || Number.isNaN(nowMs)) return "—";

  const daysAgo = calendarDaysBetween({
    earlierIso: iso,
    laterIso: now,
    timeZone,
  });

  if (daysAgo <= 0) {
    const diffMs = Math.max(0, nowMs - at);

    if (diffMs < 60000) return "just now";

    const minutes = Math.floor(diffMs / 60000);

    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    return `${hours} h ago`;
  }

  return `${daysAgo} d ago`;
}
export function dayHeading({
  iso,
  now,
  timeZone,
}: {
  readonly iso: string;
  readonly now: string;
  readonly timeZone: string;
}): string {
  const { key, monthDay } = dayKeyAndLabel({
    iso,
    timeZone,
  });
  const today = dayKeyAndLabel({
    iso: now,
    timeZone,
  }).key;
  const yesterday = dayKeyAndLabel({
    iso: new Date(Date.parse(now) - dayMs).toISOString(),
    timeZone,
  }).key;

  if (key === today) return "Today";

  if (key === yesterday) return "Yesterday";

  return monthDay;
}
export function groupByDay({
  rows,
  now,
  timeZone,
}: {
  readonly rows: readonly FeedRow[];
  readonly now: string;
  readonly timeZone: string;
}): FeedDayGroup[] {
  const sorted = rows.toSorted((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const groups: Array<{
    readonly key: string;
    readonly heading: string;
    readonly rows: FeedRow[];
  }> = [];

  for (const row of sorted) {
    const key = dayKeyAndLabel({
      iso: row.at,
      timeZone,
    }).key;
    const last = groups[groups.length - 1];

    if (last && last.key === key) {
      last.rows.push(row);
    } else {
      groups.push({
        key,
        heading: dayHeading({
          iso: row.at,
          now,
          timeZone,
        }),
        rows: [row],
      });
    }
  }

  return groups.map(({ heading, rows: groupRows }) => ({
    heading,
    rows: groupRows,
  }));
}
